import { useCallback } from 'react';
import { difference, union } from 'es-toolkit';

type FilterType = 'brands' | 'skinTypes' | 'skinConcerns' | 'ingredients';

interface UseFilterHandlersProps {
  selectedBrands: number[];
  selectedSkinTypes: number[];
  selectedSkinConcerns: number[];
  selectedIngredients: number[];
  setSelectedBrands: (brands: number[]) => void;
  setSelectedSkinTypes: (skinTypes: number[]) => void;
  setSelectedSkinConcerns: (skinConcerns: number[]) => void;
  setSelectedIngredients: (ingredients: number[]) => void;
  syncUrlWithFilters: (
    newState: Partial<{
      brands: number[];
      skinTypes: number[];
      skinConcerns: number[];
      ingredients: number[];
    }>,
  ) => void;
}

export const useFilterHandlers = ({
  selectedBrands,
  selectedSkinTypes,
  selectedSkinConcerns,
  selectedIngredients,
  setSelectedBrands,
  setSelectedSkinTypes,
  setSelectedSkinConcerns,
  setSelectedIngredients,
  syncUrlWithFilters,
}: UseFilterHandlersProps) => {
  const createFilterHandler = useCallback(
    (filterType: FilterType, isSingleSelection = false) => {
      return (termId: number, checked: boolean) => {
        let newValues: number[];
        let updateState: (values: number[]) => void;
        let urlKey: FilterType;

        switch (filterType) {
          case 'brands':
            newValues = isSingleSelection
              ? checked
                ? [termId]
                : []
              : checked
                ? union(selectedBrands, [termId])
                : difference(selectedBrands, [termId]);
            updateState = setSelectedBrands;
            urlKey = 'brands';
            break;
          case 'skinTypes':
            newValues = checked ? union(selectedSkinTypes, [termId]) : difference(selectedSkinTypes, [termId]);
            updateState = setSelectedSkinTypes;
            urlKey = 'skinTypes';
            break;
          case 'skinConcerns':
            newValues = checked ? union(selectedSkinConcerns, [termId]) : difference(selectedSkinConcerns, [termId]);
            updateState = setSelectedSkinConcerns;
            urlKey = 'skinConcerns';
            break;
          case 'ingredients':
            newValues = checked ? union(selectedIngredients, [termId]) : difference(selectedIngredients, [termId]);
            updateState = setSelectedIngredients;
            urlKey = 'ingredients';
            break;
          default:
            return;
        }

        updateState(newValues);
        syncUrlWithFilters({ [urlKey]: newValues });
      };
    },
    [
      selectedBrands,
      selectedSkinTypes,
      selectedSkinConcerns,
      selectedIngredients,
      setSelectedBrands,
      setSelectedSkinTypes,
      setSelectedSkinConcerns,
      setSelectedIngredients,
      syncUrlWithFilters,
    ],
  );

  const handleBrandChange = useCallback(
    (termId: number, checked: boolean) => createFilterHandler('brands', true)(termId, checked),
    [createFilterHandler],
  );

  const handleSkinTypeChange = useCallback(
    (termId: number, checked: boolean) => createFilterHandler('skinTypes')(termId, checked),
    [createFilterHandler],
  );

  const handleSkinConcernChange = useCallback(
    (termId: number, checked: boolean) => createFilterHandler('skinConcerns')(termId, checked),
    [createFilterHandler],
  );

  const handleIngredientChange = useCallback(
    (termId: number, checked: boolean) => createFilterHandler('ingredients')(termId, checked),
    [createFilterHandler],
  );

  return {
    handleBrandChange,
    handleSkinTypeChange,
    handleSkinConcernChange,
    handleIngredientChange,
  };
};
